/**
 * Dimensional Analysis drill bank.
 *
 * Each problem ships with an authoritative factor chain so the engine can
 * grade student answers AND show step-by-step reasoning. Numeric tolerance
 * is set per-problem because some categories (temperature, mole conversions)
 * are sensitive to rounding.
 */

export type DACategory =
  | "length"
  | "mass"
  | "volume"
  | "time"
  | "metric-prefix"
  | "mol-gram"
  | "mol-liter-stp"
  | "mol-particles"
  | "density"
  | "multi-step";

export type DADifficulty = "easy" | "medium" | "hard";

export interface DAFactor {
  /** Numerator label, e.g. "1 m" */
  num: string;
  /** Denominator label, e.g. "100 cm" */
  den: string;
  /** Numerical multiplier this factor contributes (num / den). */
  value: number;
}

export interface DAProblem {
  id: string;
  category: DACategory;
  difficulty: DADifficulty;
  prompt: string;
  givenValue: number;
  givenUnit: string;
  targetUnit: string;
  /** Ordered conversion factors student should chain. */
  chain: DAFactor[];
  /** Final numerical answer (target units). */
  answer: number;
  /** Sig-figs to round display answer to. */
  sigFigs: number;
  /** Acceptable relative tolerance for grading (0.02 = 2%). */
  tolerance: number;
  /** One-line teaching note shown after grading. */
  insight: string;
}

export const CATEGORY_LABELS: Record<DACategory, string> = {
  length: "Length",
  mass: "Mass",
  volume: "Volume",
  time: "Time",
  "metric-prefix": "Metric Prefixes",
  "mol-gram": "Moles ↔ Grams",
  "mol-liter-stp": "Moles ↔ Liters (STP)",
  "mol-particles": "Moles ↔ Particles",
  density: "Density",
  "multi-step": "Multi-step Chains",
};

function f(num: string, den: string, value: number): DAFactor {
  return { num, den, value };
}

export const DA_PROBLEMS: DAProblem[] = [
  // ── length ──────────────────────────────────────────────────────────────
  {
    id: "len-1",
    category: "length",
    difficulty: "easy",
    prompt: "Convert 250 cm to meters.",
    givenValue: 250,
    givenUnit: "cm",
    targetUnit: "m",
    chain: [f("1 m", "100 cm", 1 / 100)],
    answer: 2.5,
    sigFigs: 3,
    tolerance: 0.005,
    insight: "Centi- means ×10⁻². 100 cm = 1 m, always.",
  },
  {
    id: "len-2",
    category: "length",
    difficulty: "easy",
    prompt: "Convert 3.4 km to meters.",
    givenValue: 3.4,
    givenUnit: "km",
    targetUnit: "m",
    chain: [f("1000 m", "1 km", 1000)],
    answer: 3400,
    sigFigs: 4,
    tolerance: 0.005,
    insight: "Kilo- means ×10³. Multiply by 1000 going km → m.",
  },
  {
    id: "len-3",
    category: "length",
    difficulty: "medium",
    prompt: "Convert 5.2 inches to centimeters. (1 in = 2.54 cm)",
    givenValue: 5.2,
    givenUnit: "in",
    targetUnit: "cm",
    chain: [f("2.54 cm", "1 in", 2.54)],
    answer: 13.208,
    sigFigs: 3,
    tolerance: 0.01,
    insight: "2.54 cm/in is the defined exact conversion — memorise it.",
  },

  // ── mass ────────────────────────────────────────────────────────────────
  {
    id: "mass-1",
    category: "mass",
    difficulty: "easy",
    prompt: "Convert 1500 g to kilograms.",
    givenValue: 1500,
    givenUnit: "g",
    targetUnit: "kg",
    chain: [f("1 kg", "1000 g", 1 / 1000)],
    answer: 1.5,
    sigFigs: 3,
    tolerance: 0.005,
    insight: "1 kg = 1000 g. Going small→big, divide.",
  },
  {
    id: "mass-2",
    category: "mass",
    difficulty: "medium",
    prompt: "Convert 4.5 kg to milligrams.",
    givenValue: 4.5,
    givenUnit: "kg",
    targetUnit: "mg",
    chain: [f("1000 g", "1 kg", 1000), f("1000 mg", "1 g", 1000)],
    answer: 4_500_000,
    sigFigs: 3,
    tolerance: 0.005,
    insight: "Two prefix hops = ×10⁶. Always chain factors, never combine in your head.",
  },

  // ── volume ──────────────────────────────────────────────────────────────
  {
    id: "vol-1",
    category: "volume",
    difficulty: "easy",
    prompt: "Convert 750 mL to liters.",
    givenValue: 750,
    givenUnit: "mL",
    targetUnit: "L",
    chain: [f("1 L", "1000 mL", 1 / 1000)],
    answer: 0.75,
    sigFigs: 3,
    tolerance: 0.005,
    insight: "1 L = 1000 mL. Common in titration calculations.",
  },
  {
    id: "vol-2",
    category: "volume",
    difficulty: "medium",
    prompt: "Convert 2.5 L to cubic centimeters. (1 mL = 1 cm³)",
    givenValue: 2.5,
    givenUnit: "L",
    targetUnit: "cm³",
    chain: [f("1000 mL", "1 L", 1000), f("1 cm³", "1 mL", 1)],
    answer: 2500,
    sigFigs: 4,
    tolerance: 0.005,
    insight: "1 mL ≡ 1 cm³ exactly. Useful when density is in g/cm³.",
  },

  // ── time ────────────────────────────────────────────────────────────────
  {
    id: "time-1",
    category: "time",
    difficulty: "easy",
    prompt: "Convert 180 seconds to minutes.",
    givenValue: 180,
    givenUnit: "s",
    targetUnit: "min",
    chain: [f("1 min", "60 s", 1 / 60)],
    answer: 3,
    sigFigs: 3,
    tolerance: 0.01,
    insight: "60 s = 1 min. Time conversions are not metric — memorise the ratios.",
  },
  {
    id: "time-2",
    category: "time",
    difficulty: "hard",
    prompt: "Convert 2.5 days to seconds.",
    givenValue: 2.5,
    givenUnit: "d",
    targetUnit: "s",
    chain: [
      f("24 h", "1 d", 24),
      f("60 min", "1 h", 60),
      f("60 s", "1 min", 60),
    ],
    answer: 216_000,
    sigFigs: 3,
    tolerance: 0.005,
    insight: "Build the chain unit by unit — never skip from days to seconds in one step.",
  },

  // ── metric prefix ────────────────────────────────────────────────────────
  {
    id: "pre-1",
    category: "metric-prefix",
    difficulty: "medium",
    prompt: "Convert 0.025 mg to micrograms.",
    givenValue: 0.025,
    givenUnit: "mg",
    targetUnit: "μg",
    chain: [f("1000 μg", "1 mg", 1000)],
    answer: 25,
    sigFigs: 3,
    tolerance: 0.005,
    insight: "milli (10⁻³) → micro (10⁻⁶) is a factor of 1000.",
  },
  {
    id: "pre-2",
    category: "metric-prefix",
    difficulty: "hard",
    prompt: "Convert 4.2 nm to meters in scientific notation.",
    givenValue: 4.2,
    givenUnit: "nm",
    targetUnit: "m",
    chain: [f("1 m", "1e9 nm", 1e-9)],
    answer: 4.2e-9,
    sigFigs: 2,
    tolerance: 0.01,
    insight: "nano = 10⁻⁹. Always express tiny answers in scientific notation.",
  },

  // ── mol ↔ gram ───────────────────────────────────────────────────────────
  {
    id: "mg-1",
    category: "mol-gram",
    difficulty: "easy",
    prompt: "How many moles are in 18.0 g of water? (M(H₂O) = 18.02 g/mol)",
    givenValue: 18.0,
    givenUnit: "g H₂O",
    targetUnit: "mol H₂O",
    chain: [f("1 mol H₂O", "18.02 g H₂O", 1 / 18.02)],
    answer: 0.999,
    sigFigs: 3,
    tolerance: 0.01,
    insight: "Molar mass is the bridge: g ÷ M = mol.",
  },
  {
    id: "mg-2",
    category: "mol-gram",
    difficulty: "medium",
    prompt: "What is the mass of 2.50 mol NaCl? (M(NaCl) = 58.44 g/mol)",
    givenValue: 2.5,
    givenUnit: "mol NaCl",
    targetUnit: "g NaCl",
    chain: [f("58.44 g NaCl", "1 mol NaCl", 58.44)],
    answer: 146.1,
    sigFigs: 4,
    tolerance: 0.01,
    insight: "mol × M = g. Units of M (g/mol) tell you which way to chain.",
  },

  // ── mol ↔ liter (STP) ────────────────────────────────────────────────────
  {
    id: "ml-1",
    category: "mol-liter-stp",
    difficulty: "easy",
    prompt: "How many liters does 0.500 mol of an ideal gas occupy at STP?",
    givenValue: 0.5,
    givenUnit: "mol gas",
    targetUnit: "L gas",
    chain: [f("22.4 L", "1 mol", 22.4)],
    answer: 11.2,
    sigFigs: 3,
    tolerance: 0.005,
    insight: "At STP, 1 mol of any ideal gas = 22.4 L. The classic shortcut.",
  },
  {
    id: "ml-2",
    category: "mol-liter-stp",
    difficulty: "medium",
    prompt: "Convert 5.60 L of CO₂ at STP to moles.",
    givenValue: 5.6,
    givenUnit: "L CO₂",
    targetUnit: "mol CO₂",
    chain: [f("1 mol", "22.4 L", 1 / 22.4)],
    answer: 0.25,
    sigFigs: 3,
    tolerance: 0.01,
    insight: "STP only: 22.4 L/mol breaks down at non-standard T or P.",
  },

  // ── mol ↔ particles ──────────────────────────────────────────────────────
  {
    id: "mp-1",
    category: "mol-particles",
    difficulty: "easy",
    prompt: "How many molecules are in 0.250 mol of CO₂?",
    givenValue: 0.25,
    givenUnit: "mol",
    targetUnit: "molecules",
    chain: [f("6.022e23 molecules", "1 mol", 6.022e23)],
    answer: 1.5055e23,
    sigFigs: 3,
    tolerance: 0.01,
    insight: "Avogadro's number 6.022×10²³ converts moles ↔ particles.",
  },
  {
    id: "mp-2",
    category: "mol-particles",
    difficulty: "hard",
    prompt: "How many atoms are in 3.01×10²² molecules of H₂O?",
    givenValue: 3.01e22,
    givenUnit: "molecules H₂O",
    targetUnit: "atoms",
    chain: [f("3 atoms", "1 molecule H₂O", 3)],
    answer: 9.03e22,
    sigFigs: 3,
    tolerance: 0.01,
    insight: "H₂O has 3 atoms per molecule. Particle ratios depend on the formula.",
  },

  // ── density ──────────────────────────────────────────────────────────────
  {
    id: "den-1",
    category: "density",
    difficulty: "medium",
    prompt: "What is the mass of 25.0 mL of mercury? (ρ = 13.6 g/mL)",
    givenValue: 25,
    givenUnit: "mL Hg",
    targetUnit: "g Hg",
    chain: [f("13.6 g", "1 mL", 13.6)],
    answer: 340,
    sigFigs: 3,
    tolerance: 0.01,
    insight: "Density g/mL is a conversion factor between mass and volume.",
  },
  {
    id: "den-2",
    category: "density",
    difficulty: "hard",
    prompt: "What volume (in mL) does 50.0 g of ethanol occupy? (ρ = 0.789 g/mL)",
    givenValue: 50,
    givenUnit: "g ethanol",
    targetUnit: "mL ethanol",
    chain: [f("1 mL", "0.789 g", 1 / 0.789)],
    answer: 63.37,
    sigFigs: 3,
    tolerance: 0.01,
    insight: "Flip the density when going from mass to volume.",
  },

  // ── multi-step chains ───────────────────────────────────────────────────
  {
    id: "ms-1",
    category: "multi-step",
    difficulty: "hard",
    prompt:
      "How many molecules are in 9.00 g of water? (M(H₂O) = 18.02 g/mol)",
    givenValue: 9.0,
    givenUnit: "g H₂O",
    targetUnit: "molecules",
    chain: [
      f("1 mol H₂O", "18.02 g", 1 / 18.02),
      f("6.022e23 molecules", "1 mol", 6.022e23),
    ],
    answer: 3.008e23,
    sigFigs: 3,
    tolerance: 0.01,
    insight: "g → mol → particles. Two factors, not one shortcut.",
  },
  {
    id: "ms-2",
    category: "multi-step",
    difficulty: "hard",
    prompt:
      "What volume (L) does 8.80 g of CO₂ occupy at STP? (M(CO₂) = 44.01 g/mol)",
    givenValue: 8.8,
    givenUnit: "g CO₂",
    targetUnit: "L CO₂",
    chain: [
      f("1 mol CO₂", "44.01 g", 1 / 44.01),
      f("22.4 L", "1 mol", 22.4),
    ],
    answer: 4.479,
    sigFigs: 3,
    tolerance: 0.01,
    insight: "Mass → mol → volume. Always route through moles for gas problems.",
  },
];

/** Format a number to N significant figures (returns a string). */
export function toSigFigs(value: number, sig: number): string {
  if (!isFinite(value) || value === 0) return value.toString();
  const abs = Math.abs(value);
  if (abs >= 1e6 || abs < 1e-3) return value.toExponential(sig - 1);
  return Number(value.toPrecision(sig)).toString();
}

/** Parse a free-form answer string ("4.5e-9", "4.5 × 10^-9", "4.5"). */
export function parseAnswer(input: string): number | null {
  const cleaned = input
    .trim()
    .replace(/×|x/gi, "*")
    .replace(/\s+/g, "")
    .replace(/\*10\^(-?\d+)/g, "e$1")
    .replace(/\*10(-?\d+)/g, "e$1");
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/** Grade a student's numeric answer using per-problem relative tolerance. */
export function gradeAnswer(student: number, problem: DAProblem): boolean {
  if (problem.answer === 0) return Math.abs(student) < problem.tolerance;
  const rel = Math.abs((student - problem.answer) / problem.answer);
  return rel <= problem.tolerance;
}